'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Brand } from '@/components/Brand';
import { LangProvider } from '@/lib/i18n';
import type { Lang } from '@/lib/i18n';
import { isLang } from '@/lib/i18n/types';

export default function ConfidentialitePage() {
  const [lang, setLang] = useState<Lang>('fr');
  useEffect(() => {
    const v = new URLSearchParams(window.location.search).get('lang');
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (isLang(v)) setLang(v);
  }, []);
  return (
    <LangProvider value={lang}>
      <Body lang={lang} />
    </LangProvider>
  );
}

function Body({ lang }: { lang: Lang }) {
  const isFr = lang === 'fr';
  const homeHref = isFr ? '/' : '/?lang=en';
  return (
    <div className="flex flex-col min-h-full">
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur pt-[env(safe-area-inset-top)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link href={homeHref} aria-label={isFr ? "Retour à l'accueil" : 'Back to home'}>
            <Brand size="md" />
          </Link>
          <Link href={homeHref} className="text-sm font-medium text-muted-foreground hover:text-foreground">
            {isFr ? '← Retour' : '← Back'}
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12 space-y-6">
        <h1
          className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight"
          style={{ color: 'var(--color-ocean)' }}
        >
          {isFr ? 'Confidentialité' : 'Privacy'}
        </h1>

        <p className="text-sm leading-relaxed text-foreground/85">
          {isFr
            ? "FanGass est conçu pour respecter ta vie privée. La page que tu lis explique précisément ce que le site fait (et ne fait pas) avec tes données, sans jargon juridique inutile."
            : "FanGass is built to respect your privacy. This page explains exactly what the site does (and doesn’t) with your data, no useless legal jargon."}
        </p>

        <section className="space-y-2">
          <h2 className="font-heading font-bold text-lg text-foreground">
            {isFr ? 'Ce qu’on ne fait pas' : 'What we don’t do'}
          </h2>
          <ul className="list-disc list-outside pl-5 text-sm leading-relaxed text-foreground/85 space-y-1">
            <li>
              <strong>{isFr ? 'Pas de cookies publicitaires' : 'No advertising cookies'}</strong>
              {isFr
                ? " ni de retargeting. Aucun pixel Facebook, aucune régie pub, rien."
                : ' or retargeting. No Facebook pixel, no ad networks, nothing.'}
            </li>
            <li>
              <strong>{isFr ? 'Pas de compte utilisateur.' : 'No user account.'}</strong>
              {isFr
                ? ' Aucune connexion, aucun mot de passe, aucune adresse e-mail collectée.'
                : ' No login, no password, no email collection.'}
            </li>
            <li>
              <strong>{isFr ? 'Pas de revente de données.' : 'No data resale.'}</strong>
              {isFr
                ? ' Rien n’est partagé avec des partenaires commerciaux.'
                : ' Nothing is shared with commercial partners.'}
            </li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="font-heading font-bold text-lg text-foreground">
            {isFr ? 'Mesure d’audience' : 'Analytics'}
          </h2>
          <p className="text-sm leading-relaxed text-foreground/85">
            {isFr
              ? 'Pour savoir combien de personnes utilisent le site et sur quelles plages tu cliques le plus, on utilise '
              : 'To know how many people use the site and which beaches you tap most, we use '}
            <strong>Google Analytics 4</strong>
            {isFr
              ? '. La mesure est minimale : pas de profilage publicitaire, pas de retargeting, pas de partage avec des annonceurs. Google reçoit un identifiant de visite anonyme et l’adresse IP tronquée (anonymisation activée par défaut sur GA4).'
              : '. Measurement is minimal: no advertising profiling, no retargeting, no sharing with advertisers. Google receives an anonymous visit identifier and a truncated IP address (anonymization enabled by default on GA4).'}
          </p>
          <p className="text-sm leading-relaxed text-foreground/85">
            {isFr
              ? 'Si tu préfères ne pas être compté, tu peux activer '
              : 'If you’d rather not be counted, you can enable '}
            <strong>Do Not Track</strong>
            {isFr
              ? ' dans ton navigateur, installer l’extension officielle d’opt-out Google ('
              : ' in your browser, install the official Google opt-out extension ('}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
            >
              tools.google.com/dlpage/gaoptout
            </a>
            {isFr
              ? '), ou utiliser un bloqueur classique type uBlock, la mesure tombe à zéro et le site continue de fonctionner exactement pareil.'
              : '), or use a standard blocker like uBlock, measurement drops to zero and the site keeps working exactly the same.'}
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-heading font-bold text-lg text-foreground">
            {isFr ? 'Ce qu’on fait' : 'What we do'}
          </h2>
          <p className="text-sm leading-relaxed text-foreground/85">
            {isFr
              ? "Quand tu votes sur l’état d’une plage, le serveur calcule un "
              : 'When you vote on a beach’s state, the server computes a '}
            <strong>{isFr ? 'hachage cryptographique de ton adresse IP' : 'cryptographic hash of your IP'}</strong>
            {isFr
              ? " (SHA-256 avec un sel interne au projet, irréversible) et l’associe à la plage que tu as votée. C’est la seule donnée pseudonymisée stockée, et elle a un seul rôle : t’empêcher de voter plusieurs fois pour la même plage le même jour."
              : ' (SHA-256 with a project-internal salt, irreversible) and links it to the beach you voted for. That hash is the only pseudonymised data stored, and it serves a single purpose: stopping you from voting multiple times for the same beach on the same day.'}
          </p>
          <ul className="list-disc list-outside pl-5 text-sm leading-relaxed text-foreground/85 space-y-1">
            <li>
              <strong>{isFr ? 'Conservation' : 'Retention'}</strong>
              {isFr
                ? ' : 24 heures maximum. Le fichier est archivé par jour, donc tes votes d’hier ne sont déjà plus rattachables à toi demain.'
                : ': 24 hours max. The file is archived per day, so your votes from yesterday can no longer be linked to you tomorrow.'}
            </li>
            <li>
              <strong>{isFr ? 'Aucun lien' : 'No link'}</strong>
              {isFr
                ? ' entre ce hash et une donnée nominative (pas d’e-mail, pas de nom).'
                : ' between this hash and any personal data (no email, no name).'}
            </li>
            <li>
              <strong>{isFr ? 'Géolocalisation' : 'Geolocation'}</strong>
              {isFr
                ? ' : non utilisée. Le hash IP n’est jamais géolocalisé ni vendu.'
                : ': not used. The IP hash is never geolocated or sold.'}
            </li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="font-heading font-bold text-lg text-foreground">
            {isFr ? 'Sous-traitants techniques' : 'Technical subprocessors'}
          </h2>
          <ul className="list-disc list-outside pl-5 text-sm leading-relaxed text-foreground/85 space-y-1">
            <li>
              <strong>Vercel</strong>
              {isFr
                ? '(hébergement), peut conserver des logs serveur standards (IP, user-agent) pendant quelques jours pour la sécurité et la prévention d’abus.'
                : '(hosting), may retain standard server logs (IP, user-agent) for a few days for security and abuse prevention.'}
            </li>
            <li>
              <strong>Anthropic</strong>
              {isFr
                ? '(génération automatique du bulletin), appelé uniquement côté serveur, ne reçoit aucune donnée personnelle des visiteurs.'
                : '(automatic forecast generation), called server-side only, receives no personal data from visitors.'}
            </li>
            <li>
              <strong>OpenStreetMap</strong>
              {isFr
                ? '(tuiles cartographiques), quand la carte se charge, ton navigateur télécharge des images depuis leurs serveurs.'
                : '(map tiles), when the map loads, your browser downloads images from their servers.'}
            </li>
            <li>
              <strong>Google Analytics 4</strong>
              {isFr
                ? '(mesure d’audience anonymisée), voir la section dédiée ci-dessus.'
                : '(anonymized analytics), see the section above.'}
            </li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="font-heading font-bold text-lg text-foreground">
            {isFr ? 'Tes droits (RGPD)' : 'Your rights (GDPR)'}
          </h2>
          <p className="text-sm leading-relaxed text-foreground/85">
            {isFr
              ? "Tu disposes d’un droit d’accès, de rectification, d’opposition, de limitation et d’effacement de tes données personnelles. Vu qu’on ne stocke qu’un hash IP non réversible pendant 24h, en pratique tes données s’effacent toutes seules dès le lendemain. Pour toute demande, contacte Morgan via le bouton WhatsApp en bas de la page d’accueil ou via "
              : 'You have rights of access, rectification, objection, restriction and erasure for your personal data. Since we only store a non-reversible IP hash for 24 hours, in practice your data deletes itself the next day. For any request, contact Morgan via the WhatsApp button at the bottom of the home page or via '}
            <a
              href="https://www.linkedin.com/in/morganhaillus/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
            >
              LinkedIn
            </a>
            {isFr ? '. Tu peux également déposer une réclamation auprès de la ' : '. You can also lodge a complaint with the '}
            <a
              href="https://www.cnil.fr/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
            >
              CNIL
            </a>
            .
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-heading font-bold text-lg text-foreground">
            {isFr ? 'Cookies' : 'Cookies'}
          </h2>
          <p className="text-sm leading-relaxed text-foreground/85">
            {isFr ? 'Le site dépose un petit nombre de cookies :' : 'The site sets a small number of cookies:'}
          </p>
          <ul className="list-disc list-outside pl-5 text-sm leading-relaxed text-foreground/85 space-y-1">
            <li>
              <strong>{isFr ? 'Cookies strictement nécessaires' : 'Strictly necessary cookies'}</strong>
              {isFr
                ? ' au fonctionnement (par exemple, le routage Next.js). Ils n’identifient pas la personne et sont, selon la CNIL, exemptés de consentement.'
                : ' for the site to work (e.g. Next.js routing). They don’t identify the person and, according to the CNIL, are exempt from consent.'}
            </li>
            <li>
              <strong>{isFr ? 'Cookies Google Analytics' : 'Google Analytics cookies'}</strong>
              {isFr
                ? ' (préfixe '
                : ' (prefix '}
              <code className="font-mono text-[11px]">_ga</code>
              {isFr
                ? ') pour la mesure d’audience anonymisée. Ils ne servent qu’à compter les visiteurs uniques. Tu peux les bloquer avec n’importe quel bloqueur de cookies.'
                : ') for anonymized analytics. They only count unique visitors. You can block them with any cookie blocker.'}
            </li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="font-heading font-bold text-lg text-foreground">
            {isFr
              ? 'Île et langue par défaut'
              : 'Default island and language'}
          </h2>
          <p className="text-sm leading-relaxed text-foreground/85">
            {isFr
              ? 'À ta première visite, le site choisit une île et une langue par défaut adaptées : le pays approximatif est déduit de ton adresse IP au niveau du serveur (sans la stocker, sans la conserver, sans la revendre), et la langue de ton navigateur est lue. Par exemple, une connexion depuis la Martinique ouvre directement sur la Martinique. Tu peux changer d’île ou de langue en un clic, et ce choix est mémorisé dans ton navigateur (stockage local) pour les visites suivantes — rien n’est envoyé à un serveur. Cette personnalisation est purement fonctionnelle : selon la CNIL, elle ne nécessite pas de consentement.'
              : 'On your first visit, the site picks a sensible default island and language: your approximate country is derived from your IP at the server level (never stored, kept, or sold) and your browser language is read. For example, a connection from Martinique opens directly on Martinique. You can change island or language in one click, and that choice is remembered in your browser (local storage) for next time — nothing is sent to a server. This personalisation is purely functional: per the CNIL, it requires no consent.'}
          </p>
        </section>

        <p className="text-xs text-muted-foreground pt-4">
          {isFr ? 'Dernière mise à jour : juin 2026.' : 'Last updated: June 2026.'}
        </p>
      </main>

      <footer className="border-t border-border bg-card mt-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
          <Link href={homeHref} className="hover:text-foreground">
            {isFr ? 'Accueil' : 'Home'}
          </Link>
          <span>·</span>
          <Link
            href={isFr ? '/mentions-legales' : '/mentions-legales?lang=en'}
            className="hover:text-foreground"
          >
            {isFr ? 'Mentions légales' : 'Legal notice'}
          </Link>
          <span>·</span>
          <Link
            href={isFr ? '/confidentialite' : '/confidentialite?lang=en'}
            className="hover:text-foreground"
          >
            {isFr ? 'Confidentialité' : 'Privacy'}
          </Link>
        </div>
      </footer>
    </div>
  );
}
